'use client';
import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';

import { ThemeProvider, useTheme } from '@/lib/themeContext';
import SocialShareButtons from '@/components/SocialShareButtons';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import { createClient } from '@/lib/supabase/client';

// ─── TYPES ───────────────────────────────────────────────────────────────────
interface JobOffer {
  id: string;
  title: string;
  company: string;
  location: string;
  hourly_rate_gross: number | null;
  start_date: string | null;
  description: string | null;
  shift_pattern: string | null;
  working_hours: string | null;
  required_gender: string | null;
  couples_allowed: boolean | null;
  driving_license_required: boolean | null;
  required_english_level: string | null;
  housing_cost_monthly: number | null;
  transport_cost_monthly: number | null;
  health_insurance_cost_monthly: number | null;
}

// ─── EU LANGUAGES ───────────────────────────────────────────────────────────
const EU_LANGUAGES: { code: string; name: string; flag: string }[] = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺' },
  { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
  { code: 'sk', name: 'Slovenčina', flag: '🇸🇰' },
  { code: 'bg', name: 'Български', flag: '🇧🇬' },
  { code: 'hr', name: 'Hrvatski', flag: '🇭🇷' },
  { code: 'sl', name: 'Slovenščina', flag: '🇸🇮' },
  { code: 'el', name: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰' },
  { code: 'fi', name: 'Suomi', flag: '🇫🇮' },
  { code: 'et', name: 'Eesti', flag: '🇪🇪' },
  { code: 'lv', name: 'Latviešu', flag: '🇱🇻' },
  { code: 'lt', name: 'Lietuvių', flag: '🇱🇹' },
  { code: 'mt', name: 'Malti', flag: '🇲🇹' },
  { code: 'ga', name: 'Gaeilge', flag: '🇮🇪' },
  { code: 'lb', name: 'Lëtzebuergesch', flag: '🇱🇺' },
  { code: 'cy', name: 'Cymraeg', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿' },
  { code: 'eu', name: 'Euskara', flag: '🏳️' },
];

// ─── TRANSLATIONS ────────────────────────────────────────────────────────────
type TranslationKey =
  | 'nav_home' | 'nav_about' | 'nav_how' | 'nav_jobs' | 'nav_contact' | 'hero_badge' | 'hero_title1' | 'hero_title2' | 'hero_subtitle' | 'hero_cta' | 'hero_whatsapp' | 'about_label' | 'about_title' | 'about_p1' | 'about_p2' | 'about_p3' | 'about_p4' | 'about_p5' | 'how_label' | 'how_title' | 'how_subtitle' | 'step1_title' | 'step1_desc' | 'step2_title' | 'step2_desc' | 'step3_title' | 'step3_desc' | 'step4_title' | 'step4_desc' | 'step5_title' | 'step5_desc' | 'step6_title' | 'step6_desc' | 'community_label' | 'community_title' | 'community_desc' | 'community_join' | 'community_whatsapp' | 'cta_title' | 'cta_subtitle' | 'cta_button' | 'footer_rights' | 'footer_tagline' | 'sectors_label' | 'sectors_title' | 'sectors_subtitle' | 'benefits_title' | 'benefit_housing' | 'benefit_transport' | 'benefit_health' | 'req_label' | 'req_title' | 'req_no_visa' | 'req_no_visa_desc' | 'req_eu_citizenship' | 'req_eu_citizenship_desc' | 'req_english' | 'req_english_desc';

type Translations = Record<TranslationKey, string>;

const TRANSLATIONS: Record<string, Translations> = {
  en: {
    nav_home: 'Home', nav_about: 'About', nav_how: 'How It Works', nav_jobs: 'Jobs', nav_contact: 'Contact',
    hero_badge: '🇪🇺 Exclusively for EU Citizens',
    hero_title1: 'Your Gateway to',
    hero_title2: 'European Careers',
    hero_subtitle: 'Connect with top opportunities across the Netherlands and Europe, guided by an experienced recruiter who understands the Dutch job market and your ambitions.',
    hero_cta: 'Fill the Form Here',
    hero_whatsapp: 'Chat on WhatsApp',
    about_label: 'About Us',
    about_title: 'Welcome to TUGA Recruitment',
    about_p1: 'TUGA Recruitment was founded by Nuno Barroso, dedicated to connecting European talent with career opportunities across Europe, with a strong focus on the Netherlands.',
    about_p2: 'Nuno Barroso, based in the Netherlands, brings a strong international network and deep knowledge of the Dutch job market, collaborating with more than 35 Dutch employment agencies across multiple industries.',
    about_p3: 'Nuno Barroso personally leads candidate support and communication, ensuring every applicant receives personalised guidance throughout the recruitment process. He is fluent in Portuguese, Spanish, and English.',
    about_p4: 'TUGA Recruitment focuses exclusively on candidates who hold European citizenship, offering opportunities primarily in the Netherlands, as well as in other European countries such as Germany, Belgium, Greece, and beyond.',
    about_p5: 'Whether you are looking to start a new chapter abroad or advance your career internationally, TUGA Recruitment is here to guide you with professionalism, transparency, and a human approach.',
    how_label: 'Our Process',
    how_title: 'How We Work',
    how_subtitle: 'We are committed to guiding you every step of the way in your journey toward a new professional opportunity in the Netherlands.',
    step1_title: 'Create Your Profile',
    step1_desc: 'Start by registering your profile. We strongly recommend completing your profile as thoroughly as possible and uploading an up-to-date CV in English. A detailed profile significantly increases your chances of being matched with the right opportunities.',
    step2_title: 'Initial Contact & Strategy',
    step2_desc: 'After your registration, our team will reach out to you via WhatsApp. During this conversation, we will clarify any questions and work with you to define the best strategy to support your job search in the Netherlands.',
    step3_title: 'Job Matching & Offers',
    step3_desc: 'Whenever suitable opportunities arise, you will receive job offers that align with your professional profile and experience.',
    step4_title: 'Candidate Promotion',
    step4_desc: 'Once you accept one of our job proposals, we will present your profile to our client in the most effective way possible. We only promote candidates who meet our clients\' requirements and achieve a compatibility match of at least 85%.',
    step5_title: 'Client Evaluation',
    step5_desc: 'After reviewing your profile, the client may either accept or decline your application. If accepted, the client will contact you directly to confirm details such as your arrival in the Netherlands and your start date.',
    step6_title: 'Ongoing Support & Arrival',
    step6_desc: 'Once everything is confirmed, we will continue to support you during your relocation and throughout your first week of work. We ensure your arrival goes smoothly and your working conditions are as expected.',
    community_label: 'Stay Connected',
    community_title: 'Join Our WhatsApp Community',
    community_desc: 'Stay up to date with the latest news, job offers, and useful information by joining our WhatsApp group. Be the first to know about new opportunities.',
    community_join: 'Join WhatsApp Group',
    community_whatsapp: 'Message Us Directly',
    cta_title: 'Ready to Start Your Journey?',
    cta_subtitle: 'Register now and take the first step towards your new career in the Netherlands.',
    cta_button: 'Fill the Form Here',
    footer_rights: '© 2026 TUGA Recruitment. All rights reserved.',
    footer_tagline: 'Connecting EU talent with Dutch opportunities.',
    sectors_label: 'Job Sectors',
    sectors_title: 'Where We Place You',
    sectors_subtitle: 'All our job offers are in the following sectors. Every position includes housing, transport and health insurance.',
    benefits_title: 'All Offers Include',
    benefit_housing: 'Housing',
    benefit_transport: 'Transport',
    benefit_health: 'Health Insurance',
    req_label: 'Important Requirements',
    req_title: 'Before You Apply',
    req_no_visa: '🚫 No VISA or Sponsorship',
    req_no_visa_desc: 'We do not provide VISA or sponsorship of any kind. All candidates must already have the right to work in the EU.',
    req_eu_citizenship: '🇪🇺 European Citizenship Required',
    req_eu_citizenship_desc: 'European citizenship is mandatory. Only candidates holding a valid EU passport or EU citizenship are eligible to apply.',
    req_english: '🗣️ English is Mandatory',
    req_english_desc: 'A working knowledge of English is required for all positions. English is the primary language used in the workplace.',
  },
  pt: {
    nav_home: 'Início', nav_about: 'Sobre', nav_how: 'Como Funciona', nav_jobs: 'Empregos', nav_contact: 'Contacto',
    hero_badge: '🇪🇺 Exclusivamente para Cidadãos Europeus',
    hero_title1: 'A Sua Porta de Entrada para',
    hero_title2: 'Carreiras Europeias',
    hero_subtitle: 'Conecte-se com as melhores oportunidades nos Países Baixos e na Europa, guiado por um recrutador experiente que conhece o mercado de trabalho holandês e as suas ambições.',
    hero_cta: 'Preencher o Formulário',
    hero_whatsapp: 'Falar no WhatsApp',
    about_label: 'Sobre Nós',
    about_title: 'Bem-vindo à TUGA Recruitment',
    about_p1: 'A TUGA Recruitment foi fundada por Nuno Barroso, dedicado a conectar talentos europeus com oportunidades de carreira em toda a Europa, com especial foco nos Países Baixos.',
    about_p2: 'Nuno Barroso, baseado nos Países Baixos, traz uma forte rede internacional e profundo conhecimento do mercado de trabalho holandês, colaborando com mais de 35 agências de emprego holandesas.',
    about_p3: 'Nuno Barroso lidera pessoalmente o apoio aos candidatos e a comunicação, garantindo que cada candidato recebe orientação personalizada ao longo de todo o processo. É fluente em português, espanhol e inglês.',
    about_p4: 'A TUGA Recruitment foca-se exclusivamente em candidatos com cidadania europeia, oferecendo oportunidades principalmente nos Países Baixos, bem como noutros países europeus.',
    about_p5: 'Quer esteja a procurar começar um novo capítulo no estrangeiro ou avançar na sua carreira internacionalmente, a TUGA Recruitment está aqui para o guiar.',
    how_label: 'O Nosso Processo',
    how_title: 'Como Trabalhamos',
    how_subtitle: 'Estamos comprometidos em guiá-lo em cada passo da sua jornada rumo a uma nova oportunidade profissional nos Países Baixos.',
    step1_title: 'Crie o Seu Perfil',
    step1_desc: 'Comece por registar o seu perfil. Recomendamos vivamente que complete o seu perfil o mais detalhadamente possível e que carregue um CV atualizado em inglês.',
    step2_title: 'Contacto Inicial e Estratégia',
    step2_desc: 'Após o seu registo, a nossa equipa entrará em contacto consigo via WhatsApp para definir a melhor estratégia para a sua procura de emprego.',
    step3_title: 'Correspondência e Ofertas de Emprego',
    step3_desc: 'Sempre que surgirem oportunidades adequadas, receberá ofertas de emprego alinhadas com o seu perfil profissional e experiência.',
    step4_title: 'Promoção do Candidato',
    step4_desc: 'Assim que aceitar uma das nossas propostas, apresentaremos o seu perfil ao nosso cliente da forma mais eficaz possível. Apenas promovemos candidatos com pelo menos 85% de compatibilidade.',
    step5_title: 'Avaliação pelo Cliente',
    step5_desc: 'Após analisar o seu perfil, o cliente poderá aceitar ou recusar a sua candidatura. Se aceite, o cliente entrará em contacto diretamente para confirmar os detalhes.',
    step6_title: 'Apoio Contínuo e Chegada',
    step6_desc: 'Após confirmação, continuaremos a apoiá-lo durante a sua relocalização e ao longo da sua primeira semana de trabalho.',
    community_label: 'Mantenha-se Conectado',
    community_title: 'Junte-se à Nossa Comunidade WhatsApp',
    community_desc: 'Fique a par das últimas notícias, ofertas de emprego e informações úteis juntando-se ao nosso grupo de WhatsApp.',
    community_join: 'Entrar no Grupo WhatsApp',
    community_whatsapp: 'Enviar Mensagem Direta',
    cta_title: 'Pronto para Começar a Sua Jornada?',
    cta_subtitle: 'Registe-se agora e dê o primeiro passo rumo à sua nova carreira nos Países Baixos.',
    cta_button: 'Preencher o Formulário',
    footer_rights: '© 2026 TUGA Recruitment. Todos os direitos reservados.',
    footer_tagline: 'Conectando talentos europeus com oportunidades holandesas.',
    sectors_label: 'Sectores de Emprego',
    sectors_title: 'Onde o Colocamos',
    sectors_subtitle: 'Todas as nossas ofertas de emprego são nos seguintes sectores. Cada posição inclui alojamento, transporte e seguro de saúde.',
    benefits_title: 'Todas as Ofertas Incluem',
    benefit_housing: 'Alojamento',
    benefit_transport: 'Transporte',
    benefit_health: 'Seguro de Saúde',
    req_label: 'Requisitos Importantes',
    req_title: 'Antes de se Candidatar',
    req_no_visa: '🚫 Não Fornecemos VISA nem Sponsor',
    req_no_visa_desc: 'Não fornecemos VISA nem patrocínio de qualquer tipo. Todos os candidatos devem já ter direito a trabalhar na UE.',
    req_eu_citizenship: '🇪🇺 Cidadania Europeia Obrigatória',
    req_eu_citizenship_desc: 'A cidadania europeia é obrigatória. Apenas candidatos com passaporte ou cidadania europeia válida são elegíveis.',
    req_english: '🗣️ Inglês é Obrigatório',
    req_english_desc: 'É necessário um conhecimento funcional de inglês para todas as posições. O inglês é o idioma principal utilizado no local de trabalho.',
  },
  es: {
    nav_home: 'Inicio', nav_about: 'Sobre', nav_how: 'Cómo Funciona', nav_jobs: 'Empleos', nav_contact: 'Contacto',
    hero_badge: '🇪🇺 Exclusivamente para Ciudadanos Europeos',
    hero_title1: 'Tu Puerta de Entrada a',
    hero_title2: 'Carreras Europeas',
    hero_subtitle: 'Conéctate con las mejores oportunidades en los Países Bajos y Europa, guiado por un reclutador experimentado que conoce el mercado laboral holandés.',
    hero_cta: 'Rellenar el Formulario',
    hero_whatsapp: 'Chatear en WhatsApp',
    about_label: 'Sobre Nosotros',
    about_title: 'Bienvenido a TUGA Recruitment',
    about_p1: 'TUGA Recruitment fue fundada por Nuno Barroso, dedicado a conectar el talento europeo con oportunidades de carrera en toda Europa, con especial enfoque en los Países Bajos.',
    about_p2: 'Nuno Barroso, con sede en los Países Bajos, aporta una sólida red internacional y profundo conocimiento del mercado laboral holandés, colaborando con más de 35 agencias de empleo holandesas.',
    about_p3: 'Nuno Barroso lidera personalmente el apoyo a los candidatos y la comunicación, asegurando que cada solicitante reciba orientación personalizada durante todo el proceso. Domina el portugués, español e inglés.',
    about_p4: 'TUGA Recruitment se centra exclusivamente en candidatos con ciudadanía europea, ofreciendo oportunidades principalmente en los Países Bajos.',
    about_p5: 'Ya sea que busques comenzar un nuevo capítulo en el extranjero o avanzar en tu carrera internacionalmente, TUGA Recruitment está aquí para guiarte.',
    how_label: 'Nuestro Proceso',
    how_title: 'Cómo Trabajamos',
    how_subtitle: 'Estamos comprometidos a guiarte en cada paso de tu camino hacia una nueva oportunidad profesional en los Países Bajos.',
    step1_title: 'Crea Tu Perfil',
    step1_desc: 'Comienza registrando tu perfil. Recomendamos completarlo lo más detalladamente posible y subir un CV actualizado en inglés.',
    step2_title: 'Contacto Inicial y Estrategia',
    step2_desc: 'Después de tu registro, nuestro equipo se pondrá en contacto contigo por WhatsApp para definir la mejor estrategia.',
    step3_title: 'Matching y Ofertas de Trabajo',
    step3_desc: 'Cuando surjan oportunidades adecuadas, recibirás ofertas de trabajo alineadas con tu perfil profesional.',
    step4_title: 'Promoción del Candidato',
    step4_desc: 'Una vez que aceptes una de nuestras propuestas, presentaremos tu perfil al cliente. Solo promovemos candidatos con al menos 85% de compatibilidad.',
    step5_title: 'Evaluación del Cliente',
    step5_desc: 'Tras revisar tu perfil, el cliente puede aceptar o rechazar tu candidatura. Si es aceptado, el cliente te contactará directamente.',
    step6_title: 'Apoyo Continuo y Llegada',
    step6_desc: 'Una vez confirmado todo, continuaremos apoyándote durante tu reubicación y durante tu primera semana de trabajo.',
    community_label: 'Mantente Conectado',
    community_title: 'Únete a Nuestra Comunidad de WhatsApp',
    community_desc: 'Mantente al día con las últimas noticias, ofertas de trabajo e información útil uniéndote a nuestro grupo de WhatsApp.',
    community_join: 'Unirse al Grupo de WhatsApp',
    community_whatsapp: 'Enviar Mensaje Directo',
    cta_title: '¿Listo para Comenzar Tu Viaje?',
    cta_subtitle: 'Regístrate ahora y da el primer paso hacia tu nueva carrera en los Países Bajos.',
    cta_button: 'Rellenar el Formulario',
    footer_rights: '© 2026 TUGA Recruitment. Todos los derechos reservados.',
    footer_tagline: 'Conectando talento europeo con oportunidades holandesas.',
    sectors_label: 'Sectores de Empleo',
    sectors_title: 'Dónde Te Colocamos',
    sectors_subtitle: 'Todas nuestras ofertas de empleo son en los siguientes sectores. Cada puesto incluye alojamiento, transporte y seguro médico.',
    benefits_title: 'Todas las Ofertas Incluyen',
    benefit_housing: 'Alojamiento',
    benefit_transport: 'Transporte',
    benefit_health: 'Seguro Médico',
    req_label: 'Requisitos Importantes',
    req_title: 'Antes de Postularte',
    req_no_visa: '🚫 No Proporcionamos VISA ni Sponsorship',
    req_no_visa_desc: 'No proporcionamos VISA ni patrocinio de ningún tipo. Todos los candidatos deben tener ya el derecho a trabajar en la UE.',
    req_eu_citizenship: '🇪🇺 Ciudadanía Europea Obligatoria',
    req_eu_citizenship_desc: 'La ciudadanía europea es obligatoria. Solo los candidatos con pasaporte o ciudadanía europea válida son elegibles.',
    req_english: '🗣️ Inglés es Obligatorio',
    req_english_desc: 'Se requiere un conocimiento funcional del inglés para todos los puestos. El inglés es el idioma principal en el lugar de trabajo.',
  },
  fr: {
    nav_home: 'Accueil', nav_about: 'À Propos', nav_how: 'Comment Ça Marche', nav_jobs: 'Emplois', nav_contact: 'Contact',
    hero_badge: '🇪🇺 Exclusivement pour les Citoyens Européens',
    hero_title1: 'Votre Passerelle vers',
    hero_title2: 'les Carrières Européennes',
    hero_subtitle: 'Connectez-vous aux meilleures opportunités aux Pays-Bas et en Europe, guidé par un recruteur expérimenté qui connaît le marché du travail néerlandais.',
    hero_cta: 'Remplir le Formulaire',
    hero_whatsapp: 'Discuter sur WhatsApp',
    about_label: 'À Propos',
    about_title: 'Bienvenue chez TUGA Recruitment',
    about_p1: 'TUGA Recruitment a été fondée par Nuno Barroso, dédié à connecter les talents européens avec des opportunités de carrière à travers l\'Europe, avec un fort accent sur les Pays-Bas.',
    about_p2: 'Nuno Barroso, basé aux Pays-Bas, apporte un solide réseau international et une connaissance approfondie du marché du travail néerlandais, collaborant avec plus de 35 agences d\'emploi néerlandaises.',
    about_p3: 'Nuno Barroso dirige personnellement le soutien aux candidats et la communication, veillant à ce que chaque candidat reçoive un accompagnement personnalisé tout au long du processus. Il maîtrise le portugais, l\'espagnol et l\'anglais.',
    about_p4: 'TUGA Recruitment se concentre exclusivement sur les candidats ayant la citoyenneté européenne, offrant des opportunités principalement aux Pays-Bas.',
    about_p5: 'Que vous cherchiez à commencer un nouveau chapitre à l\'étranger ou à faire avancer votre carrière à l\'international, TUGA Recruitment est là pour vous guider.',
    how_label: 'Notre Processus',
    how_title: 'Comment Nous Travaillons',
    how_subtitle: 'Nous nous engageons à vous guider à chaque étape de votre parcours vers une nouvelle opportunité professionnelle aux Pays-Bas.',
    step1_title: 'Créez Votre Profil',
    step1_desc: 'Commencez par enregistrer votre profil. Nous recommandons vivement de le compléter aussi exhaustivement que possible et de télécharger un CV à jour en anglais.',
    step2_title: 'Contact Initial et Stratégie',
    step2_desc: 'Après votre inscription, notre équipe vous contactera via WhatsApp pour définir la meilleure stratégie pour votre recherche d\'emploi.',
    step3_title: 'Matching et Offres d\'Emploi',
    step3_desc: 'Chaque fois que des opportunités appropriées se présentent, vous recevrez des offres d\'emploi correspondant à votre profil professionnel.',
    step4_title: 'Promotion du Candidat',
    step4_desc: 'Une fois que vous acceptez l\'une de nos propositions, nous présenterons votre profil à notre client. Nous ne promouvons que les candidats avec au moins 85% de compatibilité.',
    step5_title: 'Évaluation par le Client',
    step5_desc: 'Après examen de votre profil, le client peut accepter ou refuser votre candidature. S\'il est accepté, le client vous contactera directement.',
    step6_title: 'Soutien Continu et Arrivée',
    step6_desc: 'Une fois tout confirmé, nous continuerons à vous soutenir pendant votre relocalisation et tout au long de votre première semaine de travail.',
    community_label: 'Restez Connecté',
    community_title: 'Rejoignez Notre Communauté WhatsApp',
    community_desc: 'Restez informé des dernières nouvelles, offres d\'emploi et informations utiles en rejoignant notre groupe WhatsApp.',
    community_join: 'Rejoindre le Groupe WhatsApp',
    community_whatsapp: 'Envoyer un Message Direct',
    cta_title: 'Prêt à Commencer Votre Voyage?',
    cta_subtitle: 'Inscrivez-vous maintenant et faites le premier pas vers votre nouvelle carrière aux Pays-Bas.',
    cta_button: 'Remplir le Formulaire',
    footer_rights: '© 2026 TUGA Recruitment. Tous droits réservés.',
    footer_tagline: 'Connecter les talents européens aux opportunités néerlandaises.',
    sectors_label: 'Secteurs d\'Emploi',
    sectors_title: 'Où Nous Vous Plaçons',
    sectors_subtitle: 'Toutes nos offres d\'emploi sont dans les secteurs suivants. Chaque poste comprend logement, transport et assurance maladie.',
    benefits_title: 'Toutes les Offres Comprennent',
    benefit_housing: 'Logement',
    benefit_transport: 'Transport',
    benefit_health: 'Assurance Maladie',
    req_label: 'Exigences Importantes',
    req_title: 'Avant de Postuler',
    req_no_visa: '🚫 Pas de VISA ni de Sponsoring',
    req_no_visa_desc: 'Nous ne fournissons pas de VISA ni de parrainage. Tous les candidats doivent déjà avoir le droit de travailler dans l\'UE.',
    req_eu_citizenship: '🇪🇺 Citoyenneté Européenne Obligatoire',
    req_eu_citizenship_desc: 'La citoyenneté européenne est obligatoire. Seuls les candidats titulaires d\'un passeport ou d\'une citoyenneté européenne valide sont éligibles.',
    req_english: '🗣️ L\'Anglais est Obligatoire',
    req_english_desc: 'Une connaissance fonctionnelle de l\'anglais est requise pour tous les postes. L\'anglais est la langue principale utilisée sur le lieu de travail.',
  },
  de: {
    nav_home: 'Startseite', nav_about: 'Über Uns', nav_how: 'Wie Es Funktioniert', nav_jobs: 'Stellen', nav_contact: 'Kontakt',
    hero_badge: '🇪🇺 Ausschließlich für EU-Bürger',
    hero_title1: 'Ihr Tor zu',
    hero_title2: 'Europäischen Karrieren',
    hero_subtitle: 'Verbinden Sie sich mit den besten Möglichkeiten in den Niederlanden und Europa, geführt von einem erfahrenen Recruiter, der den niederländischen Arbeitsmarkt kennt.',
    hero_cta: 'Formular Ausfüllen',
    hero_whatsapp: 'WhatsApp Kontakt',
    about_label: 'Über Uns',
    about_title: 'Willkommen bei TUGA Recruitment',
    about_p1: 'TUGA Recruitment wurde von Nuno Barroso gegründet, der sich der Verbindung europäischer Talente mit Karrieremöglichkeiten in ganz Europa widmet, mit besonderem Fokus auf die Niederlande.',
    about_p2: 'Nuno Barroso, mit Sitz in den Niederlanden, bringt ein starkes internationales Netzwerk und tiefes Wissen über den niederländischen Arbeitsmarkt mit und arbeitet mit mehr als 35 niederländischen Arbeitsvermittlungsagenturen zusammen.',
    about_p3: 'Nuno Barroso leitet persönlich die Kandidatenbetreuung und Kommunikation und stellt sicher, dass jeder Bewerber während des gesamten Prozesses persönliche Unterstützung erhält. Er spricht fließend Portugiesisch, Spanisch und Englisch.',
    about_p4: 'TUGA Recruitment konzentriert sich ausschließlich auf Kandidaten mit europäischer Staatsbürgerschaft und bietet Möglichkeiten hauptsächlich in den Niederlanden.',
    about_p5: 'Ob Sie ein neues Kapitel im Ausland beginnen oder Ihre Karriere international vorantreiben möchten, TUGA Recruitment ist hier, um Sie zu führen.',
    how_label: 'Unser Prozess',
    how_title: 'Wie Wir Arbeiten',
    how_subtitle: 'Wir sind bestrebt, Sie bei jedem Schritt auf Ihrem Weg zu einer neuen beruflichen Möglichkeit in den Niederlanden zu begleiten.',
    step1_title: 'Profil Erstellen',
    step1_desc: 'Beginnen Sie mit der Registrierung Ihres Profils. Wir empfehlen dringend, Ihr Profil so vollständig wie möglich auszufüllen und einen aktuellen Lebenslauf auf Englisch hochzuladen.',
    step2_title: 'Erstkontakt und Strategie',
    step2_desc: 'Nach Ihrer Registrierung wird unser Team Sie über WhatsApp kontaktieren, um die beste Strategie für Ihre Jobsuche zu definieren.',
    step3_title: 'Job-Matching und Angebote',
    step3_desc: 'Wenn geeignete Möglichkeiten entstehen, erhalten Sie Jobangebote, die Ihrem beruflichen Profil entsprechen.',
    step4_title: 'Kandidatenförderung',
    step4_desc: 'Sobald Sie eines unserer Jobangebote annehmen, präsentieren wir Ihr Profil unserem Kunden. Wir fördern nur Kandidaten mit mindestens 85% Kompatibilität.',
    step5_title: 'Kundenbewertung',
    step5_desc: 'Nach Prüfung Ihres Profils kann der Kunde Ihre Bewerbung annehmen oder ablehnen. Bei Annahme kontaktiert der Kunde Sie direkt.',
    step6_title: 'Laufende Unterstützung und Ankunft',
    step6_desc: 'Sobald alles bestätigt ist, unterstützen wir Sie weiterhin bei Ihrem Umzug und in Ihrer ersten Arbeitswoche.',
    community_label: 'Bleiben Sie Verbunden',
    community_title: 'Treten Sie Unserer WhatsApp-Community Bei',
    community_desc: 'Bleiben Sie mit den neuesten Nachrichten, Stellenangeboten und nützlichen Informationen auf dem Laufenden, indem Sie unserer WhatsApp-Gruppe beitreten.',
    community_join: 'WhatsApp-Gruppe Beitreten',
    community_whatsapp: 'Direkte Nachricht Senden',
    cta_title: 'Bereit, Ihre Reise zu Beginnen?',
    cta_subtitle: 'Registrieren Sie sich jetzt und machen Sie den ersten Schritt zu Ihrer neuen Karriere in den Niederlanden.',
    cta_button: 'Formular Ausfüllen',
    footer_rights: '© 2026 TUGA Recruitment. Alle Rechte vorbehalten.',
    footer_tagline: 'Europäische Talente mit niederländischen Möglichkeiten verbinden.',
    sectors_label: 'Beschäftigungssektoren',
    sectors_title: 'Wo Wir Sie Platzieren',
    sectors_subtitle: 'Alle unsere Stellenangebote sind in den folgenden Sektoren. Jede Stelle beinhaltet Unterkunft, Transport und Krankenversicherung.',
    benefits_title: 'Alle Angebote Beinhalten',
    benefit_housing: 'Unterkunft',
    benefit_transport: 'Transport',
    benefit_health: 'Krankenversicherung',
    req_label: 'Wichtige Anforderungen',
    req_title: 'Vor Ihrer Bewerbung',
    req_no_visa: '🚫 Kein VISA oder Sponsoring',
    req_no_visa_desc: 'Wir stellen kein VISA oder Sponsoring bereit. Alle Kandidaten müssen bereits das Recht haben, in der EU zu arbeiten.',
    req_eu_citizenship: '🇪🇺 EU-Staatsbürgerschaft Erforderlich',
    req_eu_citizenship_desc: 'Die EU-Staatsbürgerschaft ist Pflicht. Nur Kandidaten mit einem gültigen EU-Pass oder EU-Staatsbürgerschaft sind berechtigt.',
    req_english: '🗣️ Englisch ist Pflicht',
    req_english_desc: 'Für alle Stellen sind Englischkenntnisse erforderlich. Englisch ist die Hauptsprache am Arbeitsplatz.',
  },
};

function getT(lang: string, key: TranslationKey): string {
  const t = TRANSLATIONS[lang] || TRANSLATIONS['en'];
  return t[key] || TRANSLATIONS['en'][key] || key;
}

// ─── STEPS DATA ──────────────────────────────────────────────────────────────
const STEP_KEYS: { title: TranslationKey; desc: TranslationKey; icon: string }[] = [
  { title: 'step1_title', desc: 'step1_desc', icon: '👤' },
  { title: 'step2_title', desc: 'step2_desc', icon: '💬' },
  { title: 'step3_title', desc: 'step3_desc', icon: '🎯' },
  { title: 'step4_title', desc: 'step4_desc', icon: '🚀' },
  { title: 'step5_title', desc: 'step5_desc', icon: '✅' },
  { title: 'step6_title', desc: 'step6_desc', icon: '🏠' },
];

// ─── GALLERY IMAGE TYPE ───────────────────────────────────────────────────────
interface GalleryImage {
  id: string;
  title: string;
  description: string;
  image_url: string;
  display_order: number;
  published_at: string | null;
}

// ─── GALLERY SECTION ─────────────────────────────────────────────────────────
function GallerySection({ lang }: { lang: string }) {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const labels: Record<string, Record<string, string>> = {
    en: { section_label: 'Weekly Jobs Gallery', section_title: 'Latest Opportunities', section_subtitle: 'Browse our weekly published job opportunity images. New offers added every week.', no_images: 'No gallery images published yet. Check back soon!', prev: 'Previous', next: 'Next' },
    pt: { section_label: 'Galeria Semanal', section_title: 'Últimas Oportunidades', section_subtitle: 'Veja as nossas imagens de oportunidades publicadas semanalmente. Novas ofertas adicionadas todas as semanas.', no_images: 'Nenhuma imagem publicada ainda. Volte em breve!', prev: 'Anterior', next: 'Próximo' },
    es: { section_label: 'Galería Semanal', section_title: 'Últimas Oportunidades', section_subtitle: 'Consulta nuestras imágenes de oportunidades publicadas semanalmente. Nuevas ofertas añadidas cada semana.', no_images: 'No hay imágenes publicadas aún. ¡Vuelve pronto!', prev: 'Anterior', next: 'Siguiente' },
    fr: { section_label: 'Galerie Hebdomadaire', section_title: 'Dernières Opportunités', section_subtitle: 'Parcourez nos images d\'opportunités publiées chaque semaine. De nouvelles offres ajoutées chaque semaine.', no_images: 'Aucune image publiée pour l\'instant. Revenez bientôt!', prev: 'Précédent', next: 'Suivant' },
    de: { section_label: 'Wöchentliche Galerie', section_title: 'Neueste Möglichkeiten', section_subtitle: 'Durchsuchen Sie unsere wöchentlich veröffentlichten Stellenangebots-Bilder. Jede Woche neue Angebote.', no_images: 'Noch keine Bilder veröffentlicht. Schauen Sie bald wieder vorbei!', prev: 'Zurück', next: 'Weiter' },
  };
  const l = labels[lang] || labels['en'];

  useEffect(() => {
    async function fetchImages() {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('gallery_images')
          .select('id, title, description, image_url, display_order, published_at')
          .eq('is_published', true)
          .order('display_order', { ascending: true })
          .order('published_at', { ascending: false });
        if (!error && data) setImages(data);
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    }
    fetchImages();
  }, []);

  useEffect(() => {
    if (images.length <= 1) return;
    autoPlayRef.current = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => { if (autoPlayRef.current) clearInterval(autoPlayRef.current); };
  }, [images.length]);

  useEffect(() => {
    if (!trackRef.current || images.length === 0) return;
    const itemWidth = trackRef.current.scrollWidth / images.length;
    trackRef.current.scrollTo({ left: activeIdx * itemWidth, behavior: 'smooth' });
  }, [activeIdx, images.length]);

  function goTo(idx: number) {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    setActiveIdx(idx);
    autoPlayRef.current = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % images.length);
    }, 4000);
  }

  function prev() { goTo((activeIdx - 1 + images.length) % images.length); }
  function next() { goTo((activeIdx + 1) % images.length); }

  function openLightbox(idx: number) {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    setLightboxIdx(idx);
  }

  function closeLightbox() {
    setLightboxIdx(null);
    autoPlayRef.current = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % images.length);
    }, 4000);
  }

  function lightboxPrev() {
    setLightboxIdx((prev) => (prev === null ? 0 : (prev - 1 + images.length) % images.length));
  }

  function lightboxNext() {
    setLightboxIdx((prev) => (prev === null ? 0 : (prev + 1) % images.length));
  }

  useEffect(() => {
    if (lightboxIdx === null) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') lightboxPrev();
      if (e.key === 'ArrowRight') lightboxNext();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [lightboxIdx, images.length]);

  return (
    <section id="jobs" className="py-24 bg-secondary/30 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-amber-500 text-sm font-semibold uppercase tracking-wider mb-3">{l.section_label}</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">{l.section_title}</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">{l.section_subtitle}</p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-16">
            <svg className="w-8 h-8 animate-spin text-amber-500" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : images.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="bg-card border border-border rounded-2xl p-10 max-w-md w-full text-center shadow-sm">
              <div className="text-4xl mb-4">📸</div>
              <p className="text-foreground font-semibold mb-2">{l.no_images}</p>
            </div>
          </div>
        ) : (
          <div className="relative">
            <div
              ref={trackRef}
              className="flex gap-4 overflow-x-hidden scroll-smooth"
              style={{ scrollSnapType: 'x mandatory' }}
            >
              {images.map((img, idx) => (
                <div
                  key={img.id}
                  className="flex-shrink-0 w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)]"
                  style={{ scrollSnapAlign: 'start' }}
                >
                  <div
                    className={`group bg-card border rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer ${
                      idx === activeIdx ? 'border-amber-500/60 shadow-lg shadow-amber-500/10 scale-[1.02]' : 'border-border hover:border-amber-500/30'
                    }`}
                    onClick={() => openLightbox(idx)}
                  >
                    <div className="relative aspect-video overflow-hidden bg-secondary">
                      <img
                        src={img.image_url}
                        alt={img.title || 'Job opportunity image'}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                        onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }}
                      />
                      {idx === activeIdx && (
                        <div className="absolute inset-0 ring-2 ring-amber-500/40 rounded-2xl pointer-events-none" />
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); openLightbox(idx); }}
                        className="absolute top-2 right-2 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-lg flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
                        aria-label="View full image"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
                      </button>
                    </div>
                    {(img.title || img.description) && (
                      <div className="p-4">
                        {img.title && <p className="font-semibold text-foreground text-sm">{img.title}</p>}
                        {img.description && <p className="text-xs text-muted-foreground mt-1">{img.description}</p>}
                        {img.published_at && (
                          <p className="text-xs text-amber-500 mt-1.5">
                            {new Date(img.published_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {images.length > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <button
                  onClick={prev}
                  className="w-10 h-10 rounded-full border border-border bg-card text-foreground hover:border-amber-500/50 hover:text-amber-500 flex items-center justify-center transition-colors"
                  aria-label={l.prev}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div className="flex items-center gap-2">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => goTo(idx)}
                      className={`rounded-full transition-all duration-300 ${
                        idx === activeIdx ? 'w-6 h-2.5 bg-amber-500' : 'w-2.5 h-2.5 bg-border hover:bg-amber-500/50'
                      }`}
                      aria-label={`Go to image ${idx + 1}`}
                    />
                  ))}
                </div>
                <button
                  onClick={next}
                  className="w-10 h-10 rounded-full border border-border bg-card text-foreground hover:border-amber-500/50 hover:text-amber-500 flex items-center justify-center transition-colors"
                  aria-label={l.next}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            )}
            {images.length > 1 && (
              <p className="text-center text-xs text-muted-foreground mt-3">
                {activeIdx + 1} / {images.length}
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── LIGHTBOX ── */}
      {lightboxIdx !== null && images[lightboxIdx] && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors z-10"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>

          {/* Prev button */}
          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors z-10"
              aria-label="Previous image"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
          )}

          {/* Image */}
          <div
            className="relative max-w-5xl max-h-[85vh] w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[lightboxIdx].image_url}
              alt={images[lightboxIdx].title || 'Gallery image'}
              className="max-w-full max-h-[75vh] object-contain rounded-xl shadow-2xl"
              onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }}
            />
            {(images[lightboxIdx].title || images[lightboxIdx].description) && (
              <div className="mt-4 text-center px-4">
                {images[lightboxIdx].title && (
                  <p className="text-white font-semibold text-lg">{images[lightboxIdx].title}</p>
                )}
                {images[lightboxIdx].description && (
                  <p className="text-white/70 text-sm mt-1">{images[lightboxIdx].description}</p>
                )}
              </div>
            )}
            {/* Share buttons */}
            <div className="mt-4 flex flex-col items-center gap-2">
              <p className="text-white/50 text-xs uppercase tracking-wider">Share</p>
              <SocialShareButtons
                url={images[lightboxIdx].image_url}
                title={images[lightboxIdx].title || 'TUGA Recruitment Gallery'}
                compact={true}
              />
            </div>
            {images.length > 1 && (
              <p className="text-white/50 text-xs mt-3">{lightboxIdx + 1} / {images.length}</p>
            )}
          </div>

          {/* Next button */}
          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors z-10"
              aria-label="Next image"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          )}
        </div>
      )}
    </section>
  );
}

// ─── STATS ───────────────────────────────────────────────────────────────────
const STATS = [
  { value: '35+', labelKey: 'Agency Partners' },
  { value: '27', labelKey: 'EU Languages' },
  { value: '85%+', labelKey: 'Match Rate' },
  { value: '6', labelKey: 'Countries' },
];

// ─── LANGUAGE DROPDOWN ───────────────────────────────────────────────────────
function LangDropdown({ lang, setLang }: { lang: string; setLang: (l: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = EU_LANGUAGES.find((l) => l.code === lang) || EU_LANGUAGES[0];

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border bg-card text-foreground text-sm font-medium hover:border-amber-500/50 transition-colors"
      >
        <span>{current.flag}</span>
        <span className="hidden sm:inline">{current.name}</span>
        <svg className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-52 bg-card border border-border rounded-2xl shadow-modal z-50 overflow-hidden">
          <div className="max-h-72 overflow-y-auto py-1">
            {EU_LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => { setLang(l.code); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-secondary transition-colors ${lang === l.code ? 'text-amber-500 font-semibold' : 'text-foreground'}`}
              >
                <span>{l.flag}</span>
                <span>{l.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── THEME TOGGLE ─────────────────────────────────────────────────────────────
function ThemeBtn() {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      className="w-9 h-9 rounded-xl border border-border bg-card flex items-center justify-center text-foreground hover:border-amber-500/50 transition-colors"
      aria-label="Toggle theme"
    >
      {theme === 'dark' ? (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
      ) : (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
      )}
    </button>
  );
}

// ─── PWA INSTALL INLINE BUTTON ────────────────────────────────────────────────
function PWAInstallInline() {
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [installing, setInstalling] = useState(false);
  const deferredPromptRef = useRef<Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> } | null>(null);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setIsIOS(ios);
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e as Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };
      setCanInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => { setIsInstalled(true); setCanInstall(false); });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (isIOS) { setShowIOSHint((v) => !v); return; }
    if (!deferredPromptRef.current) return;
    setInstalling(true);
    await deferredPromptRef.current.prompt();
    const { outcome } = await deferredPromptRef.current.userChoice;
    if (outcome === 'accepted') setIsInstalled(true);
    setCanInstall(false);
    deferredPromptRef.current = null;
    setInstalling(false);
  };

  if (isInstalled) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-500 font-medium">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
        App Installed
      </div>
    );
  }

  if (!canInstall && !isIOS) return null;

  return (
    <div className="flex flex-col items-center gap-2">
      <button
        onClick={handleInstall}
        disabled={installing}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-accent text-white text-sm font-semibold hover:opacity-90 transition-all hover:scale-105 shadow-md disabled:opacity-70"
      >
        {installing ? (
          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
        )}
        {isIOS ? 'Install on iPhone / iPad' : installing ? 'Installing…' : 'Install App'}
        <span className="text-xs opacity-75">📱</span>
      </button>
      {isIOS && showIOSHint && (
        <div className="flex items-center gap-2 bg-muted/60 border border-border rounded-xl px-3 py-2 text-xs text-muted-foreground max-w-xs text-center">
          <span className="text-base">⬆️</span>
          Tap <strong className="text-foreground mx-1">Share</strong> → <strong className="text-foreground mx-1">Add to Home Screen</strong>
        </div>
      )}
    </div>
  );
}

// ─── MAIN LANDING ─────────────────────────────────────────────────────────────
function LandingContent() {
  const [lang, setLang] = useState('en');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const t = (key: TranslationKey) => getT(lang, key);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSc83RmtrUDhbjehy8BEdVzM5FOouYVtHRbnOwkTc-CWRLoW1A/viewform';
  const WHATSAPP_URL = 'https://wa.me/31683785959';
  const WHATSAPP_GROUP = 'https://chat.whatsapp.com/ECM3DeVzFejB3J5Y6CGBdl';

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* ── HEADER ── */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-card/95 backdrop-blur-md border-b border-border shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="#home" className="flex items-center gap-2.5">
              <Image src="/assets/images/1-1783443493358.jpg" alt="TUGA Recruitment logo" width={116} height={86} quality={100} unoptimized className="rounded-xl object-cover" />
            </a>
            <nav className="hidden md:flex items-center gap-6">
              {[
                { label: t('nav_home'), href: '#home' },
                { label: t('nav_about'), href: '#about' },
                { label: t('nav_how'), href: '#how' },
                { label: t('nav_jobs'), href: '#jobs' },
                { label: t('nav_contact'), href: '#contact' },
              ].map((item) => (
                <a key={item.href} href={item.href} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">{item.label}</a>
              ))}
            </nav>
            <div className="hidden md:flex items-center gap-2">
              <LangDropdown lang={lang} setLang={setLang} />
              <ThemeBtn />
              <a href={FORM_URL} target="_blank" rel="noopener noreferrer" className="ml-1 text-sm font-semibold bg-amber-500 hover:bg-amber-400 text-white px-4 py-2 rounded-xl transition-colors">
                {t('hero_cta')}
              </a>
            </div>
            <div className="md:hidden flex items-center gap-2">
              <LangDropdown lang={lang} setLang={setLang} />
              <ThemeBtn />
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg text-foreground">
                {mobileMenuOpen ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                )}
              </button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-card border-b border-border px-4 py-4 space-y-2">
            {[
              { label: t('nav_home'), href: '#home' },
              { label: t('nav_about'), href: '#about' },
              { label: t('nav_how'), href: '#how' },
              { label: t('nav_jobs'), href: '#jobs' },
              { label: t('nav_contact'), href: '#contact' },
            ].map((item) => (
              <a key={item.href} href={item.href} onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-foreground py-2">{item.label}</a>
            ))}
            <a href={FORM_URL} target="_blank" rel="noopener noreferrer" className="block text-center text-sm font-semibold bg-amber-500 text-white rounded-xl py-2.5 mt-2">
              {t('hero_cta')}
            </a>
          </div>
        )}
      </header>

      {/* ── HERO ── */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden" style={{ background: 'linear-gradient(135deg, #0c1a30 0%, #152240 45%, #1a3055 100%)' }}>
        <div className="absolute top-24 right-8 w-80 h-80 bg-amber-500/8 rounded-full blur-3xl pointer-events-none animate-pulse" />
        <div className="absolute bottom-24 left-8 w-64 h-64 bg-blue-400/8 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-600/4 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #f59e0b 1px, transparent 1px)', backgroundSize: '48px 48px' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 w-full">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div className="space-y-7">
              <div className="inline-flex items-center gap-2 bg-amber-500/15 border border-amber-500/25 text-amber-400 text-sm font-medium px-3.5 py-1.5 rounded-full">
                {t('hero_badge')}
              </div>
              <h1 className="text-4xl sm:text-5xl xl:text-6xl font-bold text-white leading-tight">
                {t('hero_title1')}<br />
                <span className="text-amber-400">{t('hero_title2')}</span>
              </h1>
              <p className="text-white/65 text-lg leading-relaxed max-w-lg">{t('hero_subtitle')}</p>
              <div className="flex flex-wrap gap-3">
                <a href={FORM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-semibold px-6 py-3.5 rounded-xl transition-all hover:shadow-lg hover:shadow-amber-500/25 hover:-translate-y-0.5">
                  {t('hero_cta')}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold px-6 py-3.5 rounded-xl transition-all hover:-translate-y-0.5">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  {t('hero_whatsapp')}
                </a>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                {STATS.map((s) => (
                  <div key={s.value} className="bg-white/6 border border-white/12 rounded-xl p-3 text-center">
                    <p className="text-xl font-bold text-amber-400">{s.value}</p>
                    <p className="text-xs text-white/55 mt-0.5">{s.labelKey}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Europe Map Illustration */}
            <div className="relative flex items-center justify-center">
              <img
                src="/assets/europe-map-netherlands.svg"
                alt="Europe map showing travel routes from EU capitals to the Netherlands"
                className="w-full h-auto max-h-[480px] object-contain drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-amber-500 text-sm font-semibold uppercase tracking-wider mb-3">{t('about_label')}</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">{t('about_title')}</h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>{t('about_p1')}</p>
                <p>{t('about_p2')}</p>
                <p>{t('about_p3')}</p>
                <p>{t('about_p4')}</p>
                <p>{t('about_p5')}</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-2">
                {['🇵🇹 Português', '🇪🇸 Español', '🇬🇧 English'].map((l) => (
                  <span key={l} className="text-sm font-medium px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400">{l}</span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 bg-card border border-border rounded-2xl p-6 hover:border-amber-500/30 transition-colors">
                <div className="text-3xl mb-3">🇳🇱</div>
                <h3 className="font-bold text-foreground mb-1">Netherlands Specialist</h3>
                <p className="text-sm text-muted-foreground">Deep knowledge of the Dutch job market with 35+ agency partnerships across all major industries.</p>
              </div>
              <div className="bg-card border border-border rounded-2xl p-5 hover:border-amber-500/30 transition-colors">
                <div className="text-2xl mb-2">🇪🇺</div>
                <h3 className="font-bold text-foreground text-sm mb-1">EU Citizens Only</h3>
                <p className="text-xs text-muted-foreground">Exclusive focus on European passport holders.</p>
              </div>
              <div className="bg-card border border-border rounded-2xl p-5 hover:border-amber-500/30 transition-colors">
                <div className="text-2xl mb-2">🤝</div>
                <h3 className="font-bold text-foreground text-sm mb-1">Human Approach</h3>
                <p className="text-xs text-muted-foreground">Personal support at every step of your journey.</p>
              </div>
              <div className="bg-amber-500 rounded-2xl p-5 text-white">
                <p className="text-3xl font-bold">35+</p>
                <p className="text-sm font-medium opacity-90 mt-1">Dutch Agency Partners</p>
              </div>
              <div className="bg-card border border-border rounded-2xl p-5 hover:border-amber-500/30 transition-colors">
                <div className="text-2xl mb-2">🌍</div>
                <h3 className="font-bold text-foreground text-sm mb-1">6+ Countries</h3>
                <p className="text-xs text-muted-foreground">NL, DE, BE, GR and more.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how" className="py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-amber-500 text-sm font-semibold uppercase tracking-wider mb-3">{t('how_label')}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">{t('how_title')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t('how_subtitle')}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {STEP_KEYS.map((step, i) => (
              <div key={i} className={`bg-card border border-border rounded-2xl p-6 hover:border-amber-500/40 hover:shadow-card-hover transition-all group ${i === 0 ? 'sm:col-span-2 lg:col-span-1' : ''}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-lg flex-shrink-0">{step.icon}</div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-full">0{i + 1}</span>
                    </div>
                    <h3 className="font-bold text-foreground mb-2 group-hover:text-amber-500 transition-colors">{t(step.title)}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(step.desc)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <a href={FORM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-semibold px-8 py-3.5 rounded-xl transition-all hover:shadow-lg hover:shadow-amber-500/25 hover:-translate-y-0.5">
              {t('cta_button')}
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </a>
          </div>
        </div>
      </section>

      {/* ── SECTORS & BENEFITS ── */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-amber-500 text-sm font-semibold uppercase tracking-wider mb-3">{t('sectors_label')}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">{t('sectors_title')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t('sectors_subtitle')}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            {[
              { icon: '⚙️', label: 'Technical' },
              { icon: '🌾', label: 'Agricultural' },
              { icon: '🚚', label: 'Logistics' },
              { icon: '🏭', label: 'Production' },
              { icon: '🛎️', label: 'Services' },
              { icon: '🚗', label: 'Drivers' },
            ].map((sector) => (
              <div key={sector.label} className="bg-card border border-border rounded-2xl p-5 flex flex-col items-center gap-3 hover:border-amber-500/40 hover:shadow-card-hover transition-all text-center">
                <span className="text-3xl">{sector.icon}</span>
                <span className="font-semibold text-foreground text-sm">{sector.label}</span>
              </div>
            ))}
          </div>
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-6 sm:p-8">
            <p className="text-amber-600 dark:text-amber-400 text-sm font-semibold uppercase tracking-wider text-center mb-6">{t('benefits_title')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: '🏠', key: 'benefit_housing' as TranslationKey },
                { icon: '🚌', key: 'benefit_transport' as TranslationKey },
                { icon: '🏥', key: 'benefit_health' as TranslationKey },
              ].map((b) => (
                <div key={b.key} className="flex items-center gap-4 bg-card border border-border rounded-xl p-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-xl flex-shrink-0">{b.icon}</div>
                  <div>
                    <p className="font-bold text-foreground">{t(b.key)}</p>
                    <p className="text-xs text-muted-foreground">Included in every offer</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── REQUIREMENTS NOTICE ── */}
      <section className="py-16 bg-red-950/10 border-y border-red-500/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-red-500 text-sm font-semibold uppercase tracking-wider mb-3">⚠️ {t('req_label')}</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">{t('req_title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card border-2 border-red-500/40 rounded-2xl p-6 flex flex-col gap-3">
              <p className="text-lg font-bold text-red-500">{t('req_no_visa')}</p>
              <p className="text-muted-foreground text-sm leading-relaxed">{t('req_no_visa_desc')}</p>
            </div>
            <div className="bg-card border-2 border-amber-500/40 rounded-2xl p-6 flex flex-col gap-3">
              <p className="text-lg font-bold text-amber-500">{t('req_eu_citizenship')}</p>
              <p className="text-muted-foreground text-sm leading-relaxed">{t('req_eu_citizenship_desc')}</p>
            </div>
            <div className="bg-card border-2 border-blue-500/40 rounded-2xl p-6 flex flex-col gap-3">
              <p className="text-lg font-bold text-blue-500">{t('req_english')}</p>
              <p className="text-muted-foreground text-sm leading-relaxed">{t('req_english_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── GALLERY / JOBS ── */}
      <GallerySection lang={lang} />

      {/* ── WHATSAPP COMMUNITY ── */}
      <section id="contact" className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-amber-500 text-sm font-semibold uppercase tracking-wider mb-3">{t('community_label')}</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">{t('community_title')}</h2>
              <p className="text-muted-foreground leading-relaxed mb-8">{t('community_desc')}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href={WHATSAPP_GROUP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold px-6 py-3.5 rounded-xl transition-all hover:shadow-lg hover:shadow-green-500/25 hover:-translate-y-0.5">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  {t('community_join')}
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-card border border-border hover:border-amber-500/50 text-foreground font-semibold px-6 py-3.5 rounded-xl transition-all hover:-translate-y-0.5">
                  <svg className="w-5 h-5 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  {t('community_whatsapp')}
                </a>
              </div>
            </div>
            <div className="bg-card border border-border rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#25D366]/10 border border-[#25D366]/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </div>
                <div>
                  <p className="font-bold text-foreground">WhatsApp</p>
                  <p className="text-sm text-muted-foreground">+31 6 83 78 59 59</p>
                </div>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                Reach out directly to Nuno on WhatsApp for any questions about job opportunities, the application process, or life in the Netherlands.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><span className="text-green-500">✓</span> Fast response time</div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><span className="text-green-500">✓</span> Available in PT, ES, EN</div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><span className="text-green-500">✓</span> Free consultation</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20 relative" style={{ background: 'linear-gradient(135deg, #0c1a30 0%, #152240 50%, #1a3055 100%)' }}>
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #f59e0b 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <p className="text-amber-400 text-sm font-semibold uppercase tracking-wider mb-4">🇪🇺 EU Citizens</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('cta_title')}</h2>
          <p className="text-white/60 mb-8 text-lg">{t('cta_subtitle')}</p>
          <a href={FORM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-bold px-8 py-4 rounded-xl text-lg transition-all hover:shadow-xl hover:shadow-amber-500/30 hover:-translate-y-1">
            {t('cta_button')}
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
          </a>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-card border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-8 pb-8 border-b border-border">
            <SocialShareButtons />
            <PWAInstallInline />
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <Image src="/assets/images/1-1783436619319.jpg" alt="TUGA Recruitment" width={32} height={32} className="rounded-lg object-cover" />
              <span className="font-bold text-foreground">TUGA <span className="text-amber-500">Recruitment</span></span>
            </div>
            <p className="text-sm text-muted-foreground text-center">{t('footer_tagline')}</p>
            <p className="text-xs text-muted-foreground">{t('footer_rights')}</p>
          </div>
        </div>
      </footer>

      {/* ── FLOATING WHATSAPP ── */}
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] hover:bg-[#20bd5a] rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-110 hover:shadow-2xl" aria-label="Contact on WhatsApp">
        <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </a>

      <PWAInstallPrompt />
    </div>
  );
}

// ─── EXPORT ───────────────────────────────────────────────────────────────────
export default function HomePage() {
  return (
    <ThemeProvider>
      <LandingContent />
    </ThemeProvider>
  );
}